<template>
    <div class="fullscreen-overlay">
        <div class="modal-container">
            <div v-if="isLoading || submitError" class="whiteout-overlay">
                <img v-if="isLoading" src="@/assets/svg/12-dots-scale-rotate-magecdn.svg" class="report-loading-spinner" alt="Loading..."/>
                <div v-else class="report-error-toast">
                    <span>{{ submitError }}</span>
                    <button class="report-error-toast-dismiss" @click="dismissError">&#x2715;</button>
                </div>
            </div>
            <div class="report-form">
                <div class="report-form-header">
                    <h1>Noise Monitoring Analysis</h1>
                </div>
                <div class="report-body">
                    <div class="modal-section">
                        <div class="modal-section-column">
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Reviewer:&nbsp;</p>
                                <p class="modal-section-column-content">{{ fullname }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Analysis Date:&nbsp;</p>
                                <p>{{ currentDate }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Company:&nbsp;</p>
                                <input type="text" v-model="editableCompany" class="single-line-input"/>
                            </div>
                        </div>
                        <div class="modal-section-column">
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Event Date:&nbsp;</p>
                                <p class="modal-section-column-content">{{ formatted_event_date }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Event Time:&nbsp;</p>
                                <p class="modal-section-column-content">{{ formatted_event_time }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">NIAC:&nbsp;</p>
                                <input type="text" v-model.number="niac" class="single-line-input" @keydown="only_numbers"/>
                            </div>
                        </div>
                        <div class="modal-section-column">
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Site:&nbsp;</p>
                                <p class="modal-section-column-content">{{ site }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Unit&nbsp;</p>
                                <p class="modal-section-column-content">{{ unit }}</p>
                            </div>
                            <div class="modal-section-column-row">
                                <p class="modal-section-column-label">Analysis Trigger:&nbsp;</p>
                                <select v-model="analysisTrigger" class="report-form-analysis-trigger-input">
                                    <option value="">Select trigger...</option>
                                    <option v-for="option in analysisTriggerOptions" :key="option" :value="option">{{ option }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <hr>
                    <div class="report-form-section">
                        <div class="report-free-text-area">
                            <textarea v-model="comments" class="report-textbox-multline" rows="5" placeholder="Additional Comments"></textarea>
                        </div>
                        <div class="report-free-text-area">
                            <textarea v-model="prevailingMeteorology" class="report-textbox-multline" rows="5" placeholder="Prevailing Meterology"></textarea>
                        </div>
                    </div>
                    <hr>
                    <h3>Sources</h3>
                    <div class="report-form-sources-container">
                        <div v-for="(sourceName, index) in Object.keys(source_comments_object)" :key="sourceName || index" class="report-form-source">
                            <div class="report-form-source-label">
                                <p>{{ sourceName }}</p>
                            </div>
                            <div class="report-form-source-input">
                                <input type="text" v-model="source_comments_object[sourceName]" class="single-line-input" placeholder="Comments" :id="sourceName + '_comment'"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="report-form-footer">
                    <ul v-if="validationErrors.length" class="report-form-validation-errors">
                        <li v-for="error in validationErrors" :key="error">{{ error }}</li>
                    </ul>
                    <button class="item-button" @click="$emit('close')">Close</button>
                    <button class="item-button" @click="submitForm()">Submit</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {ref} from 'vue';
export default {
    name: 'GenerateReportModal',
    emits: ['close', 'submit_report'],
    props: {
        event_datetime: {
            type: String,
            required: true
        },
        unit: {
            type: String,
            required: true
        },
        site: {
            type: String,
            required: true
        },
        profile: {
            type: Object,
            required: true
        },
        analysisTriggerOptions: {
            type: Array,
            required: false,
            default: () => ["Alarm review", "Noise complaint investigation", "Proactive surveillance"]
        },
        sourcesMap: {type: Array, required: true},
        regionMap: {type: Array, required: true},
        weightedSpectra: {type: Object, required: true},
    },
    setup(props){
        // Parse the token object into a format to get the user data from it.
        const tokenParsed = props.profile.keycloak?.tokenParsed || {};
        const fullname = tokenParsed.name;
        const editableCompany = ref('');
        const analysisTrigger = ref('');
        const niac = ref('');
        const validationErrors = ref([]);
        const isLoading = ref(false);
        const submitError = ref(null);
        const prevailingMeteorology = ref('');
        const comments = ref('');

        const currentDate = ref(new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }));
        const currentDateTime = ref(new Date().toISOString());

        // To get our preferred datetime format we can either set to en-gb and offset back to utc or
        // format as you see below.
        const format_event_date = (event_datetime) => {
            return new Date(event_datetime).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'UTC',
            });
        };
        const format_event_time = (event_datetime) => {
            return new Date(event_datetime).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC',
            });
        };

        const formatted_event_date = format_event_date(props.event_datetime);
        const formatted_event_time = format_event_time(props.event_datetime);

        const source_comments = (sources) => {
            const source_comments = {};
            for(const source_selection of Object.values(sources)){
                for(const source of source_selection){
                    source_comments[source.name] = "";
                }
            }
            return source_comments;
        }
        const source_comments_object = ref(source_comments(props.sourcesMap));

        const format_sources_with_regions = (sources, regions) => {
            const sources_selections = Object.values(sources);
            const formatted_sources_with_regions = [];
            for(let i = 0; i < sources_selections.length; i++){
                const source_selection_array = sources_selections[i];
                const source_selection_regions = regions[i] || [];
                for(const source_selection of source_selection_array){
                    const source_selection_object = new Object();
                    source_selection_object.source_name = source_selection.name;
                    source_selection_object.regions = [];
                    for (const region_selection of source_selection_regions) {
                        const region_selection_object = {
                            // JS doesn't have a native way to handle these datetimes without local TZ conversion
                            // So we'll just pass the strings to the backend and handle it there.
                            start : region_selection[0],
                            end : region_selection[1],
                            formula : source_selection.formula
                        };
                        source_selection_object.regions.push(region_selection_object)
                    }
                    formatted_sources_with_regions.push(source_selection_object)
                }}
            return formatted_sources_with_regions
        };
        const formatted_source_regions = ref(format_sources_with_regions(props.sourcesMap, props.regionMap));

        const format_weighted_spectra = (weightedSpectra) => {
            const formatted_weighted_spectra = {};
            const z_weighted_spectra = weightedSpectra.Z;
            for (const [frequency, value] of Object.entries(z_weighted_spectra)){
                const frequency_string = frequency.toString().replace("Z-", "");
                formatted_weighted_spectra[frequency_string] = value;
            }
            return formatted_weighted_spectra;
        }
        const formatted_weighted_spectra = ref(format_weighted_spectra(props.weightedSpectra));

        return {
            fullname,
            unit: props.unit,
            site: props.site,
            editableCompany,
            niac,
            analysisTrigger,
            analysisTriggerOptions: props.analysisTriggerOptions,
            comments,
            currentDate,
            currentDateTime,
            formatted_event_date,
            formatted_event_time,
            prevailingMeteorology,
            source_comments_object,
            formatted_source_regions,
            formatted_weighted_spectra,
            validationErrors,
            isLoading,
            submitError
        }
    },
    methods: {
        only_numbers(event) {
            const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '.', '-'];
            if (!allowed.includes(event.key) && isNaN(Number(event.key))) {
                event.preventDefault();
            }
        },

        load_comments(){
            const comment_object = {
                "general_comments": this.comments,
                "source_comments": this.source_comments_object
            }
            return comment_object;
        },

        validate_submission(){
            const errors = [];
            if (!this.editableCompany.trim()) {
                errors.push('Company is required.');
            }
            if (!this.analysisTrigger) {
                errors.push('Analysis trigger must be selected.');
            }
            if (this.niac === '' || this.niac === null || isNaN(Number(this.niac))) {
                errors.push('NIAC must be a valid number.');
            }
            if (Object.keys(this.source_comments_object).length < 1) {
                errors.push('At least one source is required.');
            }
            this.validationErrors = errors;
            return errors.length === 0;
        },

        onReportSuccess() {
            this.isLoading = false;
        },

        onReportError(message) {
            this.isLoading = false;
            this.submitError = message || 'Report generation failed. Please try again.';
        },

        dismissError() {
            this.submitError = null;
        },

        submitForm(){
            if (!this.validate_submission()) {
                return;
            }
            const reportData = {
                reviewer: this.fullname,
                unit: this.unit,
                site: this.site,
                company: this.editableCompany,
                event_datetime: this.event_datetime.toString(),
                analysis_datetime: this.currentDateTime,
                comments: this.load_comments(),
                niac: parseFloat(this.niac),
                analysis_trigger: this.analysisTrigger,
                weather: this.prevailingMeteorology,
                sources: this.formatted_source_regions,
                tot_a: this.$props.weightedSpectra.TOT_A,
                tot_c: this.$props.weightedSpectra.TOT_C,
                weightedSpectra: this.formatted_weighted_spectra
            };
            this.isLoading = true;
            this.submitError = null;
            this.$emit('submit_report', reportData);
        }
    }
};
</script>

<style scoped>
/* Update modal width to handle the 3 column layout */
.modal-container {
    width: 80vw;
}

.report-loading-spinner {
    width: 20%;
    height: 20%;
}

.report-error-toast {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    border: 1px solid var(--colour-error);
    color: var(--colour-error);
    border-radius: var(--radius-md);
    padding: var(--spacing-md);
    font-size: 1rem;
    max-width: 80%;
    background-color: var(--colour-bg-light);
}

.report-error-toast-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--colour-error);
    font-size: 1rem;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
}

.report-form {
    min-width: 80%;
}

.report-form-header {
    flex: 1;
    padding: 0;
}

.report-form-footer {
    flex: 1;
    padding: 0;
}

.report-form-validation-errors {
    color: var(--colour-error);
    text-align: left;
    padding: var(--spacing-sm) var(--spacing-ml);
    margin: 0 0 var(--spacing-sm);
    list-style: disc;
    font-size: 0.9rem;
}

.report-form-section {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
}

.single-line-input {
    width: 100%;
    border: 1px solid var(--colour-border);
    border-radius: var(--radius-sm);
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 1rem;
    box-sizing: border-box;
}

.report-form-analysis-trigger-input {
    border: 1px solid var(--colour-border);
    border-radius: var(--radius-sm);
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 1rem;
    flex: 1 2 auto;
    margin-left: var(--spacing-xs);
    background-color: white;
    box-sizing: border-box;
    min-width: 0;
}

.report-form-sources-container {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.report-form-source {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
    padding: var(--spacing-xs);
}

.report-form-source-label {
    flex: 0 0 15%;
    align-items: flex-start;
}

.report-form-source-label p {
    text-align: left;
    font-weight: bold;
}

.report-form-source-input {
    flex: 1 1 auto;
    padding-left: var(--spacing-sm);
}

.report-free-text-area {
    width: 50%;
    padding: var(--spacing-xs);
}

.report-free-text-area textarea {
    width: 100%;
    border: 1px solid var(--colour-border);
    border-radius: var(--radius-sm);
    padding: var(--spacing-sm);
    font-size: 1rem;
    font-family: inherit;
    resize: none;
}

hr {
    margin: var(--spacing-lg) 0;
    border: none;
    border-top: 2px solid var(--colour-border-strong);
}

h3 {
    text-align: left;
}
</style>
